import * as React from 'react';
import { Box, Button, Divider, IconButton, MenuItem, Switch, TextField, Typography } from '@material-ui/core';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import { FormSectionCard } from '../../components/cards/FormSectionCard';
import { TASKFLOW_ON_COMPLETION_STATUS_OPTIONS, TaskflowOnCompletionRule } from '../types';
import { ConfigureTabProps } from './ConfigureTabProps';

const GREEN = '#2e7d32';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fieldRow: {
            marginBottom: theme.spacing(2.5),
        },
        label: {
            fontSize: 11.5,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: theme.spacing(0.75),
        },
        help: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.75),
        },
        ruleTable: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            overflow: 'hidden',
        },
        ruleHeadRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 24px 1fr auto',
            gap: theme.spacing(1.25),
            alignItems: 'center',
            padding: theme.spacing(1, 1.5),
            backgroundColor: theme.palette.grey[50],
            fontSize: 10.5,
            fontWeight: 700,
            color: theme.palette.text.disabled,
            textTransform: 'uppercase',
        },
        ruleHeadRowCond: {
            gridTemplateColumns: '130px 1fr 24px 1fr auto',
        },
        ruleRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 24px 1fr auto',
            gap: theme.spacing(1.25),
            alignItems: 'center',
            padding: theme.spacing(1, 1.5),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        ruleRowCond: {
            gridTemplateColumns: '130px 1fr 24px 1fr auto',
        },
        arrow: {
            textAlign: 'center',
            color: theme.palette.text.disabled,
        },
        emptyState: {
            padding: theme.spacing(2),
            fontSize: 12,
            color: theme.palette.text.disabled,
            textAlign: 'center',
        },
        addButton: {
            width: '100%',
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 700,
            padding: theme.spacing(1, 1.5),
            borderTop: `1px dashed ${theme.palette.divider}`,
            borderRadius: 0,
        },
        divider: {
            margin: theme.spacing(3, 0),
        },
        toggleRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            padding: theme.spacing(1.5, 1.75),
            backgroundColor: theme.palette.grey[50],
        },
        toggleTitle: {
            fontSize: 13,
            fontWeight: 700,
        },
        toggleDesc: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: 2,
            maxWidth: 420,
        },
    })
);

const generateRuleId = () => `oc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const OnCompletionTab: React.FC<ConfigureTabProps> = ({ form, values, taskflow }) => {
    const classes = useStyles();

    const allTasks = taskflow.taskOrder.map(taskId => taskflow.tasks[taskId]).filter(Boolean);
    const hasOutcome = !!values.outcome;
    const outcomeOptions = values.outcome?.options ?? ['Yes', 'No'];

    const toggleOutcome = (checked: boolean) => {
        if (checked) {
            form.change('outcome', { question: values.outcome?.question ?? '', options: ['Yes', 'No'] });
        } else {
            form.batch(() => {
                form.change('outcome', undefined);
                form.change('onCompletionRules', values.onCompletionRules.filter(rule => !rule.ifOutcome));
            });
        }
    };

    return (
        <FormSectionCard
            title="On Completion"
            description="What happens automatically when this task is marked complete."
            color={GREEN}
            bgColor="#e8f5e9"
            icon={PlaylistAddCheckIcon}
        >
            <FieldArray name="onCompletionRules">
                {({ fields }) => {
                    const rules: TaskflowOnCompletionRule[] = fields.value ?? [];
                    const alwaysEntries = rules.map((rule, index) => ({ rule, index })).filter(entry => !entry.rule.ifOutcome);
                    const conditionalEntries = rules.map((rule, index) => ({ rule, index })).filter(entry => !!entry.rule.ifOutcome);

                    return (
                        <>
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.label}>When this task is completed, always</Typography>
                                <Box className={classes.ruleTable}>
                                    <Box className={classes.ruleHeadRow}>
                                        <span>Set task</span><span /><span>To status</span><span />
                                    </Box>
                                    {alwaysEntries.length === 0 ? (
                                        <Typography className={classes.emptyState}>No on-completion rules for this task.</Typography>
                                    ) : alwaysEntries.map(({ rule, index }) => (
                                        <Box key={rule.id} className={classes.ruleRow}>
                                            <Field name={`${fields.name}[${index}].fromTaskId`} subscription={{ value: true }}>
                                                {({ input }) => (
                                                    <TextField {...input} select={true} variant="outlined" size="small" fullWidth={true}>
                                                        {allTasks.map(otherTask => <MenuItem key={otherTask.id} value={otherTask.id}>{otherTask.id} · {otherTask.title}</MenuItem>)}
                                                    </TextField>
                                                )}
                                            </Field>
                                            <span className={classes.arrow}>→</span>
                                            <Field name={`${fields.name}[${index}].toStatus`} subscription={{ value: true }}>
                                                {({ input }) => (
                                                    <TextField {...input} select={true} variant="outlined" size="small" fullWidth={true}>
                                                        {TASKFLOW_ON_COMPLETION_STATUS_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                                    </TextField>
                                                )}
                                            </Field>
                                            <IconButton size="small" onClick={() => fields.remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        className={classes.addButton}
                                        startIcon={<AddIcon fontSize="small" />}
                                        onClick={() => fields.push({ id: generateRuleId(), fromTaskId: allTasks[0]?.id ?? '', toStatus: TASKFLOW_ON_COMPLETION_STATUS_OPTIONS[0] })}
                                    >
                                        Add another rule
                                    </Button>
                                </Box>
                                <Typography className={classes.help}>Fires the moment this task's status is set to Complete, regardless of any outcome below.</Typography>
                            </Box>

                            <Divider className={classes.divider} />

                            <Box className={classes.fieldRow}>
                                <Typography className={classes.label}>Set matter stage (optional)</Typography>
                                <Field name="matterStage" subscription={{ value: true }}>
                                    {({ input }) => (
                                        <TextField {...input} fullWidth={true} variant="outlined" size="small" placeholder="e.g. Letter sent" />
                                    )}
                                </Field>
                                <Typography className={classes.help}>
                                    Written to the matter's stage history the instant this task completes. Leave blank for tasks that don't mark a real milestone.
                                </Typography>
                            </Box>

                            <Divider className={classes.divider} />

                            <Box className={classes.toggleRow}>
                                <Box>
                                    <Typography className={classes.toggleTitle}>Ask a question on completion</Typography>
                                    <Typography className={classes.toggleDesc}>
                                        Answered the moment this task is marked complete — the section below can then branch on it.
                                    </Typography>
                                </Box>
                                <Switch color="primary" checked={hasOutcome} onChange={event => toggleOutcome(event.target.checked)} />
                            </Box>

                            {hasOutcome && (
                                <>
                                    <Box className={classes.fieldRow} style={{ marginTop: 20 }}>
                                        <Typography className={classes.label}>Question</Typography>
                                        <Field name="outcome.question" subscription={{ value: true }}>
                                            {({ input }) => (
                                                <TextField {...input} fullWidth={true} variant="outlined" size="small" placeholder="e.g. Draft response requested?" />
                                            )}
                                        </Field>
                                        <Typography className={classes.help}>Options: {outcomeOptions.join(' / ')}</Typography>
                                    </Box>

                                    <Box className={classes.fieldRow}>
                                        <Typography className={classes.label}>Based on the answer</Typography>
                                        <Box className={classes.ruleTable}>
                                            <Box className={`${classes.ruleHeadRow} ${classes.ruleHeadRowCond}`}>
                                                <span>If</span><span>Set task</span><span /><span>To status</span><span />
                                            </Box>
                                            {conditionalEntries.length === 0 ? (
                                                <Typography className={classes.emptyState}>No conditional rules yet.</Typography>
                                            ) : conditionalEntries.map(({ rule, index }) => (
                                                <Box key={rule.id} className={`${classes.ruleRow} ${classes.ruleRowCond}`}>
                                                    <Field name={`${fields.name}[${index}].ifOutcome`} subscription={{ value: true }}>
                                                        {({ input }) => (
                                                            <TextField {...input} select={true} variant="outlined" size="small" fullWidth={true}>
                                                                {outcomeOptions.map(option => <MenuItem key={option} value={option}>If {option}</MenuItem>)}
                                                            </TextField>
                                                        )}
                                                    </Field>
                                                    <Field name={`${fields.name}[${index}].fromTaskId`} subscription={{ value: true }}>
                                                        {({ input }) => (
                                                            <TextField {...input} select={true} variant="outlined" size="small" fullWidth={true}>
                                                                {allTasks.map(otherTask => <MenuItem key={otherTask.id} value={otherTask.id}>{otherTask.id} · {otherTask.title}</MenuItem>)}
                                                            </TextField>
                                                        )}
                                                    </Field>
                                                    <span className={classes.arrow}>→</span>
                                                    <Field name={`${fields.name}[${index}].toStatus`} subscription={{ value: true }}>
                                                        {({ input }) => (
                                                            <TextField {...input} select={true} variant="outlined" size="small" fullWidth={true}>
                                                                {TASKFLOW_ON_COMPLETION_STATUS_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                                            </TextField>
                                                        )}
                                                    </Field>
                                                    <IconButton size="small" onClick={() => fields.remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                                </Box>
                                            ))}
                                            <Button
                                                className={classes.addButton}
                                                startIcon={<AddIcon fontSize="small" />}
                                                onClick={() => fields.push({ id: generateRuleId(), fromTaskId: allTasks[0]?.id ?? '', toStatus: TASKFLOW_ON_COMPLETION_STATUS_OPTIONS[0], ifOutcome: outcomeOptions[0] })}
                                            >
                                                Add conditional rule
                                            </Button>
                                        </Box>
                                        <Typography className={classes.help}>Only fires when the answer matches.</Typography>
                                    </Box>
                                </>
                            )}
                        </>
                    );
                }}
            </FieldArray>
        </FormSectionCard>
    );
};
